import type { GridColDef } from '@mui/x-data-grid-premium';

function escapeCsv(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function triggerDownload(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Legacy: export explicit field keys (header === field). Prefer downloadGridCsv. */
export function downloadCsv(rows: Record<string, any>[], columns: string[], filename: string) {
  const header = columns.map(escapeCsv).join(',');
  const body = rows.map(r => columns.map(c => escapeCsv(r[c] ?? '')).join(',')).join('\n');
  triggerDownload(header + '\n' + body, filename);
}

/**
 * Export exactly what the grid shows: one column per GridColDef (in order),
 * header = headerName, value via the column's valueGetter/valueFormatter — so the
 * CSV always matches the visible columns (renamed vars, calculated columns, etc).
 * Columns with `skipDownload` or no data field are excluded.
 */
export function downloadGridCsv(
  rows: Record<string, any>[],
  columns: GridColDef[],
  filename: string,
) {
  const cols = columns.filter(
    c => c.field && (!c.field.startsWith('_') || c.field.startsWith('_dim_')) && !(c as { skipDownload?: boolean }).skipDownload,
  );
  const header = cols.map(c => escapeCsv(c.headerName ?? c.field)).join(',');
  const body = rows.map(row => cols.map(col => {
    let value: unknown = row[col.field];
    try {
      if (col.valueGetter) value = (col.valueGetter as any)(value, row, col, {});
      if (col.valueFormatter) value = (col.valueFormatter as any)(value, row, col, {});
    } catch {
      /* fall back to the raw value */
    }
    return escapeCsv(value);
  }).join(',')).join('\n');
  triggerDownload(header + '\n' + body, filename);
}
